import { FC, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import SoftSkillRow from './SoftSkillRow';
import AddSoftSkillDialog from './AddSoftSkillDialog';
import { IndicatorValues } from './EditIndicatorsDialog';

interface SoftSkillJunction {
  softSkill: {
    id: number;
    name: string;
  };
  indicators: IndicatorValues[];
}

interface Question {
  id: string;
  subId: string;
  type: string;
  position: number;
  content?: string | null;
  extraContent?: string | null;
  duration: number;
  isPlaceholder: boolean;
  isMandatory: boolean;
  isEditable: boolean;
  isEdited: boolean;
  softSkillJunctions: SoftSkillJunction[];
}

interface QuestionCardProps {
  question: Question;
  onAddSoftSkill: (
    questionSubId: string,
    softSkillId: number,
    indicators: IndicatorValues[]
  ) => void;
  onRemoveSoftSkill: (questionSubId: string, softSkillId: number) => void;
  onUpdateIndicators: (
    questionSubId: string,
    softSkillId: number,
    indicators: IndicatorValues[]
  ) => void;
  mutationLoading?: boolean;
}

const QuestionCard: FC<QuestionCardProps> = ({
  question,
  onAddSoftSkill,
  onRemoveSoftSkill,
  onUpdateIndicators,
  mutationLoading,
}) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const existingSoftSkillIds = question.softSkillJunctions.map(
    j => j.softSkill.id
  );

  return (
    <>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              #{question.position} &middot; {question.type}
              {question.isPlaceholder && ' (placeholder)'}
              {question.isMandatory && ' (mandatory)'}
              {question.isEdited && ' (edited)'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {question.duration}min &middot; {question.subId.slice(0, 8)}...
            </Typography>
          </Box>

          {question.content && (
            <Typography variant="body1" sx={{ mb: 1 }}>
              {question.content}
            </Typography>
          )}

          {question.extraContent && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, whiteSpace: 'pre-line' }}
            >
              {question.extraContent}
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography variant="subtitle2">
              Soft Skills ({question.softSkillJunctions.length})
            </Typography>
            <Button
              startIcon={<AddIcon />}
              size="small"
              onClick={() => setAddDialogOpen(true)}
              disabled={mutationLoading}
            >
              Add
            </Button>
          </Box>

          {question.softSkillJunctions.map(junction => (
            <SoftSkillRow
              key={junction.softSkill.id}
              junction={junction}
              onRemove={softSkillId =>
                onRemoveSoftSkill(question.subId, softSkillId)
              }
              onUpdateIndicators={(softSkillId, indicators) =>
                onUpdateIndicators(question.subId, softSkillId, indicators)
              }
              removeLoading={mutationLoading}
              updateLoading={mutationLoading}
            />
          ))}

          {question.softSkillJunctions.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: 'italic' }}
            >
              No soft skills assigned
            </Typography>
          )}
        </CardContent>
      </Card>

      <AddSoftSkillDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        existingSoftSkillIds={existingSoftSkillIds}
        onAdd={(softSkillId, indicators) => {
          onAddSoftSkill(question.subId, softSkillId, indicators);
          setAddDialogOpen(false);
        }}
        loading={mutationLoading}
      />
    </>
  );
};

export default QuestionCard;
